import { Fragment, useEffect } from "react";
import Colors from "./app/assets/styles";
import { createTheme, ThemeProvider } from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import DashboardLayout from "./app/layout/dashboard";
import AdminRoutes from "./app/routes/admin";
import PublicRoutes from "./app/routes/public";
import "@fontsource/noto-sans";
import Login from "./app/views/admin/Login/index";
import useAuth from "./app/hooks/useAuth";
import { ToasterContainer } from "./app/components/Toaster";
import "./App.css";
import Home from "./app/views/web/Home";
import About from "./app/views/web/About";
import PropertyList from "./app/views/web/Property/PropertyList";
import PropertyDetail from "./app/views/web/Property/PropertyDetail";
import AgentLogin from "./app/views/web/Login";
import AgentSignup from "./app/views/web/Signup";
import MyBooking from "./app/views/web/Property/MyBooking";
import ViewSheet from "./app/views/web/Property/ViewSheet";
import AuthServices from "./app/api/AuthServices/auth.index";

const AGENT_TAB_SESSION_KEY = "agentActiveTabId";
const AGENT_ACTIVE_TABS_KEY = "agentActiveTabs";

const getActiveAgentTabs = () => {
  try {
    return JSON.parse(localStorage.getItem(AGENT_ACTIVE_TABS_KEY) || "{}");
  } catch (error) {
    console.error("Failed to read active agent tabs", error);
    return {};
  }
};

const setActiveAgentTabs = (tabs) => {
  localStorage.setItem(AGENT_ACTIVE_TABS_KEY, JSON.stringify(tabs));
};

const createAgentTabId = () =>
  `agent-tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getNavigationType = () => {
  const navigationEntry = window.performance
    ?.getEntriesByType("navigation")
    ?.at?.(0);

  if (navigationEntry?.type) {
    return navigationEntry.type;
  }

  if (window.performance?.navigation?.type === 1) {
    return "reload";
  }

  return "navigate";
};

const theme = createTheme({
  typography: {
    fontFamily: "Noto Sans, sans-serif",
    h1: { fontSize: "72px" },
    h2: { fontSize: "60px" },
    h3: { fontSize: "48px" },
    h4: { fontSize: "36px" },
    h5: { fontSize: "24px" },
    h6: { fontSize: "18px" },
    subtitle1: { fontSize: "16px" },
    subtitle2: { fontSize: "14px", fontWeight: 400 },
    caption: { fontSize: "12px" },
  },
  palette: {
    primary: { main: Colors.primary }
  }
});

function App() {
  return (
    <Fragment>
      <ThemeProvider theme={theme}>
        <ToasterContainer />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ThemeProvider>
    </Fragment>
  );
}



function AppContent() {
  const { user, webUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const titles = {
      "/": "RR Properties | Luxury Real Estate & Commercial Offices in Dubai, UAE",
      "/about": "About Us | RR Properties Dubai",
      "/property-list": "Commercial Offices & Luxury Properties for Rent & Sale | RR Properties",
      "/login": "Admin Login | RR Properties",
      "/agent/login": "Agent Portal Login | RR Properties",
      "/agent/signup": "Agent Registration | RR Properties",
      "/my-booking": "My Bookings | RR Properties",
      "/view-sheet": "View Sheet | RR Properties",
    };

    if (location.pathname.startsWith("/property-detail/")) {
      document.title = "Property Details | RR Properties Dubai";
    } else {
      document.title = titles[location.pathname] || "RR Properties | Dubai Real Estate";
    }
  }, [location.pathname]);
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!webUser?.token) return;

    const existingTabId = sessionStorage.getItem(AGENT_TAB_SESSION_KEY);
    const isNewTabSession = !existingTabId;
    const currentTabId = existingTabId || createAgentTabId();
    const navigationType = getNavigationType();
    const activeTabs = getActiveAgentTabs();
    const isCurrentTabAlreadyActive = Boolean(activeTabs[currentTabId]);
    const hasAnotherActiveTab = Object.keys(activeTabs).some(
      (tabId) => tabId !== currentTabId
    );

    if (isNewTabSession) {
      sessionStorage.setItem(AGENT_TAB_SESSION_KEY, currentTabId);
    }

    const registerCurrentTab = () => {
      const activeTabs = getActiveAgentTabs();
      activeTabs[currentTabId] = true;
      setActiveAgentTabs(activeTabs);
    };

    const unregisterCurrentTab = () => {
      const activeTabs = getActiveAgentTabs();
      delete activeTabs[currentTabId];
      setActiveAgentTabs(activeTabs);
    };

    const shouldTrackVisit =
      navigationType !== "reload" &&
      !hasAnotherActiveTab &&
      (isNewTabSession || !isCurrentTabAlreadyActive);

    if (shouldTrackVisit) {
      AuthServices.logAgentVisit().catch((error) => {
        console.error("Failed to log agent website visit", error);
      });
    }

    registerCurrentTab();

    window.addEventListener("beforeunload", unregisterCurrentTab);
    window.addEventListener("pagehide", unregisterCurrentTab);

    return () => {
      window.removeEventListener("beforeunload", unregisterCurrentTab);
      window.removeEventListener("pagehide", unregisterCurrentTab);
    };

  }, [webUser?.token]);

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/agent/login" element={<AgentLogin />} />
      <Route path="/agent/signup" element={<AgentSignup />} />
      <Route path="/property-list" element={<PropertyList />} />

      {webUser?.token && (
        <>
          <Route path="/my-booking" element={<MyBooking />} />
          <Route path="/view-sheet" element={<ViewSheet />} />

          <Route path="/property-detail/:id" element={<PropertyDetail />} />
        </>
      )}

      {user?.role === "admin" ? (
        <Route element={<DashboardLayout />}>
          {AdminRoutes.map((item, i) => (
            <Route key={i} path={item.path} element={item.component} />
          ))}
        </Route>
      ) : (
        <Route element={user ? <DashboardLayout /> : <Navigate to="/" />}>
          {PublicRoutes.map((item, i) => (
            <Route key={i} path={item.path} element={item.component} />
          ))}
        </Route>
      )}
    </Routes>
  );
}

export default App;
